import { defineComponent as _defineComponent } from 'vue'
import InputGroup from '@/components/form/InputGroup.vue';
import JoinedInputWrapper from '@/components/form/JoinedInputWrapper.vue';
import TimePicker from '@/components/inputs/TimePicker.vue';
import i18n from '@/i18n';
import { dateTimeFormats } from '@/lang/dateTimeFormats';
import {
  getErrorMessages,
  required as requiredValidation,
} from '@/plugins/vuelidate';
import store from '@/store';
import { setTimeOnDate } from '@/util/dateFunctions';
import { useVuelidate } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import spacetime from 'spacetime';
import { computed, reactive, watch, withDefaults } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'TimeRangePicker',
  props: {
    anchorDate: { default: () =>
      spacetime.now(store.getters.timezone).startOf('day').toNativeDate() },
    startTime: { default: null },
    endTime: { default: null },
    minTime: { default: null },
    maxTime: { default: null },
    startLabel: { default: i18n.tc('label.startTime') },
    endLabel: { default: i18n.tc('label.endTime') },
    disabled: { type: Boolean, default: false },
    internalValidation: { type: Boolean, default: true },
    required: { type: Boolean, default: false },
    startTimeErrors: { default: () => [] },
    endTimeErrors: { default: () => [] },
    minuteInterval: { default: 5 },
    fixedWidth: { type: Boolean, default: true },
    fullWidth: { type: Boolean, default: false }
  },
  emits: ["input", "blur"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'input', value: { startTime: Date | null; endTime: Date | null }): void;
  (e: 'blur', value: { startTime: Date | null; endTime: Date | null }): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { anchorDate: Date, startTime: Date | null, endTime: Date | null, minTime: Date | null, maxTime: Date | null, startLabel: string, endLabel: string, disabled: boolean, internalValidation: boolean, required: boolean, startTimeErrors: string[], endTimeErrors: string[], minuteInterval: number, fixedWidth: boolean, fullWidth: boolean }





const timezone = computed(() => store.getters.timezone);

const formValues = reactive({
  startTime: props.startTime,
  endTime: props.endTime,
});

const validations = computed(() => {
  if (!props.internalValidation) {
    return {
      startTime: {},
      endTime: {},
    };
  }
  const getMinTime = () => props.minTime ?? props.anchorDate;

  const getMaxTime = () =>
    props.maxTime ??
    spacetime(props.anchorDate, timezone.value)
      .add(1, 'day') // If start time is 23:59, end time can be 23:59 of the next day
      .endOf('day')
      .toNativeDate();

  const getStartMaxTime = () =>
    new Date(Math.min(+(formValues.endTime ?? getMaxTime()), +getMaxTime()));

  const getEndMinTime = () =>
    new Date(Math.max(+(formValues.startTime ?? getMinTime()), +getMinTime()));

  return {
    startTime: {
      required: requiredValidation,
      minValueInclusive: helpers.withMessage(
        () =>
          i18n.tc('errorMessage.validation.isGreaterOrEqualThanValue', 1, {
            value: i18n.d(getMinTime(), dateTimeFormats.hourMinute),
          }),
        (value: Date | null) => !value || value >= getMinTime(),
      ),
      maxValue: helpers.withMessage(
        () =>
          i18n.tc('errorMessage.validation.isLessThanValue', 1, {
            value: i18n.d(getStartMaxTime(), dateTimeFormats.hourMinute),
          }),
        (value: null | Date) => !value || value < getStartMaxTime(),
      ),
    },
    endTime: {
      required: requiredValidation,
      minValue: helpers.withMessage(
        () =>
          i18n.tc('errorMessage.validation.isGreaterThanValue', 1, {
            value: i18n.d(getEndMinTime(), dateTimeFormats.hourMinute),
          }),
        (value: Date | null) => !value || value > getEndMinTime(),
      ),
      maxValueInclusive: helpers.withMessage(
        () =>
          i18n.tc('errorMessage.validation.isLessOrEqualThanValue', 1, {
            value: i18n.d(getMaxTime(), dateTimeFormats.hourMinute),
          }),
        (value: null | Date) => !value || value <= getMaxTime(),
      ),
    },
  };
});
const form = useVuelidate(validations, formValues, { $autoDirty: true });

watch(
  () => ({
    startTime: formValues.startTime,
    endTime: formValues.endTime,
  }),
  (value) => {
    emit('input', value);
  },
);

const startTimeHandler = (value: Date | null) => {
  const anchor = props.minTime ?? props.anchorDate;
  const start = setTimeOnDate(value, timezone.value, anchor, anchor, true);
  formValues.startTime = start;

  if (!start) return;

  formValues.endTime = setTimeOnDate(
    formValues.endTime,
    timezone.value,
    start,
    start,
  );
};

const endTimeHandler = (value: Date | null) => {
  formValues.endTime = setTimeOnDate(
    value,
    timezone.value,
    formValues.startTime,
    formValues.startTime,
  );
};

let rendered = false;
watch(
  () => ({
    anchorDate: props.anchorDate,
    minTime: props.minTime,
    maxTime: props.maxTime,
    timezone: store.getters.timezone,
  }),
  () => {
    startTimeHandler(formValues.startTime);
    endTimeHandler(formValues.endTime);
    if (rendered) form.value.$validate();
    rendered = true;
  },
);

const blurHandler = () => {
  emit('blur', formValues);
};

return { __sfc: true,props, emit, timezone, formValues, validations, form, startTimeHandler, endTimeHandler, rendered, blurHandler, InputGroup, JoinedInputWrapper, TimePicker, dateTimeFormats, getErrorMessages, spacetime }
}

})